/**
 * Event Trace Page
 * Simplified pipeline monitor — File Service → StarRocks → Warehouse row-count reconciliation,
 * joined by crel_id. Currently covers "Events" (channel sends + custom account events);
 * Bulk API and Single API traceability are not yet implemented.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  ListChecks,
  RotateCw,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/select'
import { LoadingState, ErrorState } from '@/components/ui/loading-state'
import { cn, formatDate, formatNumber } from '@/lib/utils'
import { useAccount } from '@/contexts/AccountContext'
import { getEventTrace, getBulkApiTrace, syncEventFile, syncBulkApiCall } from '../queries/eventTrace'
import type { BulkApiTraceRow, EventFileTraceRow, EventTraceResponse, BulkApiTraceResponse } from '../types'

type SubTab = 'events' | 'bulk-api' | 'single-api'

const SUB_TABS: { key: SubTab; label: string; enabled: boolean }[] = [
  { key: 'events',     label: 'Events',    enabled: true },
  { key: 'bulk-api',   label: 'Bulk API',  enabled: true },
  { key: 'single-api', label: 'Single API', enabled: false },
]

const STATUS_COLORS: Record<string, string> = {
  DONE: 'text-green-700 bg-green-50 border-green-200',
  ERROR: 'text-red-700 bg-red-50 border-red-200',
  VALIDATION_ERROR: 'text-red-700 bg-red-50 border-red-200',
  STARTED: 'text-blue-700 bg-blue-50 border-blue-200',
  NOT_STARTED: 'text-gray-600 bg-gray-50 border-gray-200',
}

function FileStatusBadge({ status }: { status: string }) {
  const style = STATUS_COLORS[status] ?? 'text-gray-600 bg-gray-50 border-gray-200'
  return (
    <span className={cn('inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full border whitespace-nowrap', style)}>
      {status}
    </span>
  )
}

/** Combines StarRocks/Warehouse counts against the declared row count into one status. */
function matchStatus(row: EventFileTraceRow): { ok: boolean; label: string } {
  if (!row.table) return { ok: false, label: 'Unroutable' }
  if (!row.synced) return { ok: false, label: 'Not synced' }
  if (row.starrocksRowCount < 0 || row.warehouseRowCount < 0) return { ok: false, label: 'Query failed' }

  const srMatch = row.starrocksRowCount === row.rows
  const whMatch = row.warehouseRowCount === row.rows
  if (srMatch && whMatch) return { ok: true, label: 'Matched' }
  if (!srMatch && !whMatch) return { ok: false, label: 'Both gap' }
  return { ok: false, label: srMatch ? 'Warehouse gap' : 'StarRocks gap' }
}

function SyncButton({ synced, syncing, disabled, onClick }: { synced: boolean; syncing: boolean; disabled?: boolean; onClick: () => void }) {
  return (
    <Button
      variant={synced ? 'outline' : 'default'}
      size="sm"
      className="h-7 px-2 text-xs gap-1"
      disabled={syncing || disabled}
      onClick={onClick}
    >
      <RotateCw className={cn('h-3 w-3', syncing && 'animate-spin')} />
      {synced ? 'Re-sync' : 'Sync'}
    </Button>
  )
}

function EventTraceTable({
  rows,
  onSync,
  syncingId,
}: {
  rows: EventFileTraceRow[]
  onSync: (fileId: string) => void
  syncingId: string | null
}) {
  if (rows.length === 0) {
    return <p className="text-sm text-gray-400 italic text-center py-8">No event files found for this account/filter.</p>
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-2 px-3 text-gray-500 font-medium">File</th>
            <th className="text-left py-2 px-3 text-gray-500 font-medium">Created</th>
            <th className="text-left py-2 px-3 text-gray-500 font-medium">Category</th>
            <th className="text-center py-2 px-3 text-gray-500 font-medium">Status</th>
            <th className="text-right py-2 px-3 text-gray-500 font-medium">Rows</th>
            <th className="text-right py-2 px-3 text-gray-500 font-medium">SR Row Count</th>
            <th className="text-right py-2 px-3 text-gray-500 font-medium">Warehouse Row Count</th>
            <th className="text-center py-2 px-3 text-gray-500 font-medium">Match</th>
            <th className="text-center py-2 px-3 text-gray-500 font-medium">Sync</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const match = matchStatus(row)
            return (
              <tr key={row.fileId} className={cn('border-b border-gray-100 last:border-0', !match.ok && 'bg-red-50/40')}>
                <td className="py-2.5 px-3">
                  <div className="font-mono text-xs text-gray-700 truncate max-w-[220px]" title={row.fileName ?? undefined}>
                    {row.fileName ?? row.fileId}
                  </div>
                  {row.eventName && (
                    <div className="text-xs text-gray-400 mt-0.5">{row.eventName}</div>
                  )}
                </td>
                <td className="py-2.5 px-3 text-left text-xs text-gray-500 whitespace-nowrap">
                  {row.createdAt ? formatDate(row.createdAt) : '—'}
                </td>
                <td className="py-2.5 px-3">
                  <div className="text-gray-700">{row.category ?? '—'}</div>
                  {row.table && (
                    <div className="flex items-center gap-1 mt-0.5">
                      <span className="text-xs text-gray-400 font-mono">{row.table}</span>
                      {!row.verified && (
                        <span
                          className="inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 px-1 py-0 rounded"
                          title="crel_id join not empirically confirmed for this table — treat counts with caution"
                        >
                          <AlertTriangle className="h-2.5 w-2.5" /> unverified
                        </span>
                      )}
                    </div>
                  )}
                </td>
                <td className="py-2.5 px-3 text-center"><FileStatusBadge status={row.status} /></td>
                <td className="py-2.5 px-3 text-right tabular-nums">{formatNumber(row.rows)}</td>
                <td className="py-2.5 px-3 text-right tabular-nums">
                  {row.starrocksRowCount < 0 ? <span className="text-gray-400">—</span> : formatNumber(row.starrocksRowCount)}
                </td>
                <td className="py-2.5 px-3 text-right tabular-nums">
                  {row.warehouseRowCount < 0 ? <span className="text-gray-400">—</span> : formatNumber(row.warehouseRowCount)}
                </td>
                <td className="py-2.5 px-3 text-center">
                  <span className={cn(
                    'inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border whitespace-nowrap',
                    match.ok ? 'text-green-700 bg-green-50 border-green-200' : 'text-red-700 bg-red-50 border-red-200',
                  )}>
                    {match.ok ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                    {match.label}
                  </span>
                </td>
                <td className="py-2.5 px-3 text-center">
                  <SyncButton
                    synced={row.synced}
                    syncing={syncingId === row.fileId}
                    disabled={!row.table}
                    onClick={() => onSync(row.fileId)}
                  />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/** Combines StarRocks/Warehouse counts against published_count into one status. */
function bulkMatchStatus(row: BulkApiTraceRow): { ok: boolean; label: string } {
  if (!row.synced) return { ok: false, label: 'Not synced' }
  if (row.starrocksRowCount < 0 || row.warehouseRowCount < 0) return { ok: false, label: 'Query failed' }

  const srMatch = row.starrocksRowCount === row.publishedCount
  const whMatch = row.warehouseRowCount === row.publishedCount
  if (srMatch && whMatch) return { ok: true, label: 'Matched' }
  if (!srMatch && !whMatch) return { ok: false, label: 'Both gap' }
  return { ok: false, label: srMatch ? 'Warehouse gap' : 'StarRocks gap' }
}

function BulkApiTraceTable({
  rows,
  onSync,
  syncingId,
}: {
  rows: BulkApiTraceRow[]
  onSync: (requestId: string) => void
  syncingId: string | null
}) {
  if (rows.length === 0) {
    return <p className="text-sm text-gray-400 italic text-center py-8">No bulk API calls found for this account.</p>
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-2 px-3 text-gray-500 font-medium">Request</th>
            <th className="text-center py-2 px-3 text-gray-500 font-medium">Status</th>
            <th className="text-right py-2 px-3 text-gray-500 font-medium">Submitted</th>
            <th className="text-right py-2 px-3 text-gray-500 font-medium">Published</th>
            <th className="text-right py-2 px-3 text-gray-500 font-medium">SR Row Count</th>
            <th className="text-right py-2 px-3 text-gray-500 font-medium">Warehouse Row Count</th>
            <th className="text-center py-2 px-3 text-gray-500 font-medium">Match</th>
            <th className="text-center py-2 px-3 text-gray-500 font-medium">Sync</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const match = bulkMatchStatus(row)
            return (
              <tr key={row.requestId} className={cn('border-b border-gray-100 last:border-0', !match.ok && 'bg-red-50/40')}>
                <td className="py-2.5 px-3">
                  <div className="font-mono text-xs text-gray-700 truncate max-w-[220px]">{row.requestId}</div>
                  <div className="flex items-center gap-1 mt-0.5">
                    <span className="text-xs text-gray-400 font-mono">{row.endpoint ?? row.table ?? '—'}</span>
                    <span
                      className="inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 px-1 py-0 rounded"
                      title="crel_id join not empirically confirmed for this producer — ingestion_api_requests has no live traffic yet"
                    >
                      <AlertTriangle className="h-2.5 w-2.5" /> unverified
                    </span>
                  </div>
                </td>
                <td className="py-2.5 px-3 text-center"><FileStatusBadge status={row.apiStatus ?? '—'} /></td>
                <td className="py-2.5 px-3 text-right tabular-nums">{formatNumber(row.recordCount)}</td>
                <td className="py-2.5 px-3 text-right tabular-nums">{formatNumber(row.publishedCount)}</td>
                <td className="py-2.5 px-3 text-right tabular-nums">
                  {row.starrocksRowCount < 0 ? <span className="text-gray-400">—</span> : formatNumber(row.starrocksRowCount)}
                </td>
                <td className="py-2.5 px-3 text-right tabular-nums">
                  {row.warehouseRowCount < 0 ? <span className="text-gray-400">—</span> : formatNumber(row.warehouseRowCount)}
                </td>
                <td className="py-2.5 px-3 text-center">
                  <span className={cn(
                    'inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border whitespace-nowrap',
                    match.ok ? 'text-green-700 bg-green-50 border-green-200' : 'text-red-700 bg-red-50 border-red-200',
                  )}>
                    {match.ok ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                    {match.label}
                  </span>
                </td>
                <td className="py-2.5 px-3 text-center">
                  <SyncButton
                    synced={row.synced}
                    syncing={syncingId === row.requestId}
                    onClick={() => onSync(row.requestId)}
                  />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function ComingSoonPanel({ label }: { label: string }) {
  return (
    <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 py-12 text-center">
      <p className="text-sm text-gray-500">{label} traceability is not implemented yet.</p>
    </div>
  )
}

export function EventTracePage() {
  const { selectedAccountId } = useAccount()
  const accId: number = selectedAccountId && selectedAccountId !== 'ALL'
    ? parseInt(selectedAccountId, 10)
    : 7943

  const queryClient = useQueryClient()
  const [subTab, setSubTab] = useState<SubTab>('events')
  const [category, setCategory] = useState<string>('')
  const [eventName, setEventName] = useState<string>('')

  const eventTraceQueryKey = ['event-trace', accId, category, eventName]
  const bulkTraceQueryKey = ['bulk-api-trace', accId]

  const {
    data,
    isLoading,
    error,
    refetch,
    isFetching,
    dataUpdatedAt,
  } = useQuery({
    queryKey: eventTraceQueryKey,
    queryFn: () => getEventTrace({ accId, category: category || undefined, eventName: eventName || undefined }),
    staleTime: 60 * 1000,
    retry: 1,
    enabled: subTab === 'events',
  })

  const {
    data: bulkData,
    isLoading: bulkIsLoading,
    error: bulkError,
    refetch: bulkRefetch,
    isFetching: bulkIsFetching,
    dataUpdatedAt: bulkDataUpdatedAt,
  } = useQuery({
    queryKey: bulkTraceQueryKey,
    queryFn: () => getBulkApiTrace({ accId }),
    staleTime: 60 * 1000,
    retry: 1,
    enabled: subTab === 'bulk-api',
  })

  const syncFileMutation = useMutation({
    mutationFn: (fileId: string) => syncEventFile({ accId, fileId }),
    onSuccess: (updated) => {
      queryClient.setQueryData<EventTraceResponse>(eventTraceQueryKey, (prev) =>
        prev ? { ...prev, files: prev.files.map((f) => (f.fileId === updated.fileId ? updated : f)) } : prev,
      )
    },
  })

  const syncCallMutation = useMutation({
    mutationFn: (requestId: string) => syncBulkApiCall({ accId, requestId }),
    onSuccess: (updated) => {
      queryClient.setQueryData<BulkApiTraceResponse>(bulkTraceQueryKey, (prev) =>
        prev ? { ...prev, calls: prev.calls.map((c) => (c.requestId === updated.requestId ? updated : c)) } : prev,
      )
    },
  })

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-50 rounded-lg">
            <ListChecks className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Event Trace</h1>
            <p className="text-sm text-gray-500">
              File Service → StarRocks → Warehouse — Account {accId}
            </p>
          </div>
        </div>

        {subTab === 'events' && (
          <div className="flex items-center gap-3 flex-wrap justify-end">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-500 whitespace-nowrap">Category</label>
              <Select
                className="h-9 w-56 text-sm"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                options={[
                  { value: '', label: 'All categories' },
                  ...(data?.categories ?? []).map((c) => ({ value: c, label: c })),
                ]}
              />
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-500 whitespace-nowrap">Event name</label>
              <Select
                className="h-9 w-56 text-sm"
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                options={[
                  { value: '', label: 'All event names' },
                  ...(data?.eventNames ?? []).map((n) => ({ value: n, label: n })),
                ]}
              />
            </div>

            {dataUpdatedAt > 0 && (
              <span className="text-xs text-gray-400 whitespace-nowrap">
                {formatDate(new Date(dataUpdatedAt).toISOString())}
              </span>
            )}

            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching} className="gap-2">
              <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
              Refresh
            </Button>
          </div>
        )}

        {subTab === 'bulk-api' && (
          <div className="flex items-center gap-3 flex-wrap justify-end">
            {bulkDataUpdatedAt > 0 && (
              <span className="text-xs text-gray-400 whitespace-nowrap">
                {formatDate(new Date(bulkDataUpdatedAt).toISOString())}
              </span>
            )}

            <Button variant="outline" size="sm" onClick={() => bulkRefetch()} disabled={bulkIsFetching} className="gap-2">
              <RefreshCw className={cn('h-4 w-4', bulkIsFetching && 'animate-spin')} />
              Refresh
            </Button>
          </div>
        )}
      </div>

      {/* Sub-tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200">
        {SUB_TABS.map((tab) => (
          <button
            key={tab.key}
            disabled={!tab.enabled}
            onClick={() => tab.enabled && setSubTab(tab.key)}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              !tab.enabled && 'text-gray-300 cursor-not-allowed',
              tab.enabled && subTab === tab.key && 'border-indigo-600 text-indigo-600',
              tab.enabled && subTab !== tab.key && 'border-transparent text-gray-500 hover:text-gray-700',
            )}
          >
            {tab.label}
            {!tab.enabled && <span className="ml-1.5 text-[10px] text-gray-300">(later)</span>}
          </button>
        ))}
      </div>

      {subTab === 'single-api' && (
        <ComingSoonPanel label={SUB_TABS.find((t) => t.key === subTab)?.label ?? ''} />
      )}

      {subTab === 'events' && (
        <>
          {isLoading && (
            <LoadingState message="Tracing event files — StarRocks and warehouse queries may take a moment" />
          )}

          {error && (
            <ErrorState
              message={error instanceof Error ? error.message : 'Failed to load event trace'}
              onRetry={() => refetch()}
            />
          )}

          {data && (
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="px-5 py-4 border-b border-gray-100">
                <h2 className="font-semibold text-gray-900">Recent Event Files (up to 100)</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  Declared rows (file_metadata) vs matched rows in StarRocks/warehouse, joined by crel_id.
                  Click Sync on a row to check it — nothing queries StarRocks/warehouse automatically.
                </p>
              </div>
              <div className="px-2 py-2">
                <EventTraceTable
                  rows={data.files}
                  onSync={(fileId) => syncFileMutation.mutate(fileId)}
                  syncingId={syncFileMutation.isPending ? (syncFileMutation.variables ?? null) : null}
                />
              </div>
            </div>
          )}
        </>
      )}

      {subTab === 'bulk-api' && (
        <>
          {bulkIsLoading && (
            <LoadingState message="Tracing bulk API calls — StarRocks and warehouse queries may take a moment" />
          )}

          {bulkError && (
            <ErrorState
              message={bulkError instanceof Error ? bulkError.message : 'Failed to load bulk API trace'}
              onRetry={() => bulkRefetch()}
            />
          )}

          {bulkData && (
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="px-5 py-4 border-b border-gray-100">
                <h2 className="font-semibold text-gray-900">Recent Bulk API Calls (up to 100)</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  Published rows (ingestion_api_requests) vs matched rows in StarRocks/warehouse custom_events, joined by crel_id.
                  Click Sync on a row to check it — nothing queries StarRocks/warehouse automatically.
                </p>
              </div>
              <div className="px-2 py-2">
                <BulkApiTraceTable
                  rows={bulkData.calls}
                  onSync={(requestId) => syncCallMutation.mutate(requestId)}
                  syncingId={syncCallMutation.isPending ? (syncCallMutation.variables ?? null) : null}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
